import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import { useRouter } from "next/router";
import { useContext, useMemo } from "react";

import { ORDER_TYPE_ENUM } from "@/constants/enums";
import { CartDrawerContext } from "@/context/CartDrawerContext";
import useDesignType from "@/hooks/useDesignType";
import { panelWidth } from "@/theme/mixins";
import { ROUTE_MENU, ROUTE_REVIEW_ORDER } from "@/constants/routes";
import { MODAL_VIEW } from "@/constants/views";
import { Z_INDEX } from "@/constants/zIndex";
import { useAppDispatch, useAppSelector } from "@/store";
import { setOrderType, updateItemQuantity } from "@/store/slices/cartSlice";
import { openModal } from "@/store/slices/commonSlice";
import { formatCurrency } from "@/utils/format";
import { calculateOrderTotals, formatGstLabel, lineTotal } from "@/utils/pricing";
import CartOrderStatusHeader from "./CartOrderStatusHeader";

/**
 * `eG` — the drawer itself. The original styles the ROOT, not the paper, and
 * gets its width from the shared `panelWidth` mixin (`eD.f`), which is why the
 * cart, the About drawer and the wallet all measure the same at a given width.
 *
 * ```js
 * eG = styled(Drawer)(({theme}) => ({ flexShrink:0, overflow:"hidden",
 *                                     zIndex: Z_INDEX.ORDER_BAR, ...panelWidth(theme) }))
 * ```
 */
const Panel = styled(Drawer)(({ theme }) => ({
  flexShrink: 0,
  overflow: "hidden",
  zIndex: Z_INDEX.ORDER_BAR,
  ...panelWidth(theme),
}));

/**
 * `eH` — the scroll body, with its scrollbar hidden in both engines. It is a
 * `Stack`, so it is a flex column: that is what lets the totals block sit on
 * `margin-top: auto` at the foot of a short basket.
 */
const PanelBody = styled(Stack)(({ theme }) => ({
  height: "100%",
  overflow: "hidden",
  overflowY: "auto",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  backgroundColor: theme.palette.background.paper,
}));

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
}));

const Row = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
});

const Stepper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
  borderRadius: 999,
  "& button": { width: 26, height: 26, fontSize: 14 },
}));

/**
 * `eK` in `mod-91849.js` — the basket. Who you are ordering from, the
 * fulfilment time, the basket lines and the running totals with a single
 * Order & Pay action.
 *
 * **It is a MUI `Drawer`, and its variant is the last switch in the
 * `web_design_type` layer:**
 *
 * ```js
 * <eG variant={w || eg ? "temporary" : "permanent"}
 *     open={_} onClose={ei} anchor={w ? "bottom" : "right"}
 *     slotProps={{ paper: { sx: { height: "100%" } } }}>
 * ```
 *
 * `w` is `isTemporary` off the cart-drawer context (`useMediaQuery(down("lg"))`)
 * and `eg` is `web_design_type?.startsWith("MODERN")`. So:
 *
 * | | below `lg` | `lg` and up |
 * |---|---|---|
 * | `CLASSIC*` | temporary, anchored **bottom** | **permanent**, in the flow |
 * | `MODERN*` | temporary, anchored **bottom** | **temporary**, anchored right |
 *
 * and that is exactly why `MODERN*` is the only design that renders the
 * floating "View Cart" pill on the desktop — under `CLASSIC*` the basket is
 * already on screen, so there is nothing for a pill to open.
 */
export default function CartPanel() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { isTemporary, open, setOpen } = useContext(CartDrawerContext);
  const { isModernDesign } = useDesignType();
  const settings = useAppSelector((state) => state.settings.data);
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const { items, orderType, paymentMethod, address, scheduledFor } =
    useAppSelector((state) => state.cart);

  const {
    public_business_name: businessName,
    restaurant_address: storeAddress,
    currency_symbol: symbol,
    currency_code: code,
    takeaway_is_pickup: takeawayIsPickup,
  } = settings;

  const totals = useMemo(
    () =>
      calculateOrderTotals({
        items,
        orderType,
        paymentMethod,
        settings,
        deliveryCost: address?.deliveryCost ?? 0,
        distanceMeters: address?.distanceInMeters,
      }),
    [items, orderType, paymentMethod, settings, address],
  );

  const money = (value: number) => formatCurrency(value, { symbol, code });
  const isDelivery = orderType === ORDER_TYPE_ENUM.DELIVERY;
  const timeLabel = isDelivery ? "Delivery Time" : takeawayIsPickup ? "Pickup Time" : "Takeaway Time";

  /**
   * `k` — module `99916`, the tab handler. It writes the platform and rewrites
   * `?order_type=` in place; it opens NOTHING.
   *
   * It used to open `DELIVERY_INFO_MODAL` here when the new type was delivery
   * and no address was set. That was invented: it stood in for the original's
   * real prompt, which lives in the menu body (`iF`) and fires off the basket
   * rather than off this toggle — so it prompted on a channel switch and
   * stayed silent for every customer who arrived from the landing page's
   * Delivery card. `MenuScreen` now carries the real effect and this is back to
   * what the original does.
   */
  const handleOrderType = (next: typeof orderType) => {
    if (!next || next === orderType) return;
    dispatch(setOrderType(next));
    // Keep the URL in step with the persisted basket selection. Without this,
    // a customer could switch Delivery -> Takeaway, reload, and the old
    // `?order_type=delivery` query would win over the saved Takeaway choice.
    if (next !== ORDER_TYPE_ENUM.DINE_IN) {
      void router.replace(
        {
          pathname: ROUTE_MENU,
          query: {
            ...router.query,
            order_type:
              next === ORDER_TYPE_ENUM.DELIVERY ? "delivery" : "takeaway",
          },
        },
        undefined,
        { shallow: true, scroll: false },
      );
    }
  };

  // `ei` — the drawer's only close path.
  const close = () => setOpen(false);
  const isTemporaryVariant = isTemporary || isModernDesign;

  return (
    <Panel
      variant={isTemporaryVariant ? "temporary" : "permanent"}
      open={open}
      onClose={close}
      anchor={isTemporary ? "bottom" : "right"}
      PaperProps={{ sx: { height: "100%" } }}
    >
      <PanelBody>
      {/* `ex` — the FIRST child of the scroll body, and the owner of both the
          close control and the order-type tabs. The interim close `IconButton`
          and the interim `ToggleButtonGroup` that used to live here were the
          stopgaps it replaces. */}
      <CartOrderStatusHeader onClose={close} onOrderTypeChange={handleOrderType} />
      <Section>
        <Typography variant="subtitle1" fontWeight={700}>
          {businessName || "Our Restaurant"}
        </Typography>
        {storeAddress && (
          <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
            <PlaceOutlinedIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption">{storeAddress}</Typography>
          </Stack>
        )}
      </Section>
      <Divider />

      <Section>
        <Typography variant="caption" color="text.secondary">
          {timeLabel}
        </Typography>
        <Row>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <RocketLaunchIcon sx={{ fontSize: 15 }} />
            <Typography variant="body2" fontWeight={600}>
              {scheduledFor
                ? new Date(scheduledFor).toLocaleString()
                : "As soon as possible (ASAP)"}
            </Typography>
          </Stack>
          <Button size="small" onClick={() => dispatch(openModal(MODAL_VIEW.SELECT_TIME))}>
            Change
          </Button>
        </Row>
      </Section>
      <Divider />

      {/* Delivery orders need somewhere to go — surface the address here rather
          than only on the review screen. */}
      {isDelivery && (
        <>
          <Section>
            <Row>
              <Box minWidth={0}>
                <Typography variant="caption" color="text.secondary">
                  Delivering to
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {address?.line1 ?? address?.areaName ?? "No address yet"}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => dispatch(openModal(MODAL_VIEW.DELIVERY_INFO))}
              >
                {address ? "Change" : "Add"}
              </Button>
            </Row>
          </Section>
          <Divider />
        </>
      )}

      <Section>
        {items.length === 0 ? (
          <Stack alignItems="center" spacing={1} py={3} color="text.secondary">
            <ShoppingBagOutlinedIcon />
            <Typography variant="body2">Cart is empty</Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {items.map((line) => (
              <Row key={line.lineId} alignItems="flex-start">
                <Box flex={1} minWidth={0} pr={1}>
                  <Typography variant="body2" fontWeight={600}>
                    {line.name}
                  </Typography>
                  {line.modifiers.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {line.modifiers.map((m) => m.name).join(", ")}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                    <Stepper>
                      <IconButton
                        aria-label={`Remove one ${line.name}`}
                        onClick={() =>
                          dispatch(
                            updateItemQuantity({
                              lineId: line.lineId,
                              quantity: line.quantity - 1,
                            }),
                          )
                        }
                      >
                        {line.quantity === 1 ? (
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        ) : (
                          "−"
                        )}
                      </IconButton>
                      <Typography variant="caption" fontWeight={700} sx={{ minWidth: 16, textAlign: "center" }}>
                        {line.quantity}
                      </Typography>
                      <IconButton
                        aria-label={`Add another ${line.name}`}
                        onClick={() =>
                          dispatch(
                            updateItemQuantity({
                              lineId: line.lineId,
                              quantity: line.quantity + 1,
                            }),
                          )
                        }
                      >
                        +
                      </IconButton>
                    </Stepper>
                  </Stack>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {money(lineTotal(line))}
                </Typography>
              </Row>
            ))}
          </Stack>
        )}
      </Section>
      <Divider />

      <Section sx={{ mt: "auto" }}>
        <Stack spacing={0.25}>
          <Row>
            <Typography variant="body2" color="text.secondary">
              Price
            </Typography>
            <Typography variant="body2">{money(totals.subtotal)}</Typography>
          </Row>
          {totals.tax > 0 && (
            <Row>
              <Typography variant="body2" color="text.secondary">
                GST ({formatGstLabel(totals.gstRate)})
              </Typography>
              <Typography variant="body2">{money(totals.tax)}</Typography>
            </Row>
          )}
          {totals.delivery > 0 && (
            <Row>
              <Typography variant="body2" color="text.secondary">
                Delivery
              </Typography>
              <Typography variant="body2">{money(totals.delivery)}</Typography>
            </Row>
          )}
          {totals.serviceFee > 0 && (
            <Row>
              <Typography variant="body2" color="text.secondary">
                Service charge
              </Typography>
              <Typography variant="body2">{money(totals.serviceFee)}</Typography>
            </Row>
          )}
        </Stack>

        {/*
          STAFF: THE BUTTON IS THE ORDER TOTAL, and nothing else.
          "Button text want Order Total no text pay." This device cannot take
          money — the bill is settled at the POS — so the word must not appear
          on a control an operator presses. A CUSTOMER still reads
          "Order & Pay", which is the original storefront's own copy and is
          true for them: they pay at the counter or on delivery.
        */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          disableElevation
          sx={{ mt: 1.5 }}
          disabled={items.length === 0}
          onClick={() => router.push(ROUTE_REVIEW_ORDER)}
        >
          {isStaff ? money(totals.total) : <>Order &amp; Pay {money(totals.total)}</>}
        </Button>
      </Section>
      </PanelBody>
    </Panel>
  );
}
